import React, { useState, useContext, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router';
import { instance } from '../../services/api';
import { GlobalsContext } from '../../store/Globals';
import {
  Container,
  TableHeader,
  TableTitle,
  TableActions,
  SearchInput,
  Button,
  Table,
  Th,
  Td,
  Tr,
  TableFooter,
  Pagination,
  PaginationButton,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
} from './styles';

interface TableGeneratorProps {
	columns: { title: string; field: string }[];
	data: any[];
	asset: {
		tag: string;
		label: string;
		readers: string[];
		canCreate: boolean;
		dynamic: boolean;
		collection?: string;
	};
	ky: string | undefined;
	canWrite: boolean;
	isLoading: boolean;
	setIsLoading(value: boolean): void;
	handleRemoveItem: (key: string) => void;
	handlePageLimit: (pageLimit: string) => void;
	updateData(): void;
	pageLimit: number;
	searchTerm?: string;
	onSearchChange?: (term: string) => void;
	contractIdSearch?: string;
	onContractIdChange?: (contractId: string) => void;
	onNextPage?: () => void;
	onPreviousPage?: () => void;
	hasNextPage?: boolean;
	hasPreviousPage?: boolean;
}

const TableGenerator: React.FC<TableGeneratorProps> = ({
	columns,
	data,
	asset,
	ky,
	isLoading,
	canWrite,
	handleRemoveItem,
	handlePageLimit,
	setIsLoading,
	updateData,
	pageLimit,
	searchTerm: externalSearchTerm = '',
	onSearchChange,
	contractIdSearch: externalContractIdSearch = '',
	onContractIdChange,
	onNextPage,
	onPreviousPage,
	hasNextPage = false,
	hasPreviousPage = false,
}) => {
  const history = useHistory();
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const globals = useContext(GlobalsContext);
  const { standardTxList } = globals;

  const isPrivate = asset.readers && asset.readers.length > 0;

  const handleView = useCallback((item: any) => {
    history.push(`/show/${item['@key']}`);
  }, [history]);

  const handleEdit = useCallback((item: any) => {
    const eventAsset = item['@key'].split(':')[0];
    if (eventAsset) {
      history.push(`/edit/${eventAsset}/${item['@key']}`);
    }
  }, [history]);

  const handleDelete = useCallback((item: any) => {
    setSelectedItem(item);
    setDialogOpen(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!selectedItem) return;

    const eventAsset = selectedItem['@key'].split(':')[0];
    if (eventAsset) {
      setIsLoading(true);
      try {
        await instance.delete('/api/invoke/deleteAsset', {
          data: {
            key: {
              '@assetType': eventAsset,
              '@key': selectedItem['@key'],
            },
          },
        });
        handleRemoveItem(selectedItem['@key']);
        toast.success('Asset deleted successfully');
      } catch (err: any) {
        toast.error(
          `Error trying to delete asset: ${err.response?.data || err.message}`
        );
      } finally {
        setIsLoading(false);
        setDialogOpen(false);
        setSelectedItem(null);
      }
    }
  }, [selectedItem, setIsLoading, handleRemoveItem]);

  const handleRowsPerPageChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    handlePageLimit(newRowsPerPage.toString());
  }, [handlePageLimit]);

	const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const term = event.target.value;
		if (onSearchChange) {
			onSearchChange(term);
		}
	}, [onSearchChange]);

	const handleContractIdChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const contractId = event.target.value;
		if (onContractIdChange) {
			onContractIdChange(contractId);
		}
	}, [onContractIdChange]);

	return (
    <Container>
      <TableHeader>
        <TableTitle>
          {isPrivate && <i className="bx bx-lock-alt" />}
          {ky ? `History: ${ky}` : `Managing ${asset.label}`}
        </TableTitle>

		<TableActions>
			{asset.tag === 'debt' && !ky && (
				<SearchInput>
					<i className="bx bx-id-card" />
					<input
						type="text"
						placeholder="Contract ID"
						value={externalContractIdSearch}
						onChange={handleContractIdChange}
						pattern="[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}"
						title="Enter a valid UUID format"
					/>
				</SearchInput>
			)}

			<SearchInput>
				<i className="bx bx-search" />
				<input
					type="text"
					placeholder={asset.tag === 'debt' ? 'Search by name, document, description...' : 'Search...'}
					value={externalSearchTerm}
					onChange={handleSearchChange}
					disabled={asset.tag === 'debt' && !!externalContractIdSearch}
				/>
			</SearchInput>

			{!ky && standardTxList.some((tx) => tx.tag === 'createAsset') && (
            <>
              {asset.dynamic && (
                <Button
                  variant="secondary"
                  disabled={!globals.dynamicTxsAllowed}
                  onClick={() => history.push(`/${asset.tag}/edit`)}
                  title="Edit Type"
                >
                  <i className="bx bx-customize" />
                </Button>
              )}
              <Button
                variant="primary"
                disabled={!asset.canCreate}
                onClick={() => history.push(`/${asset.tag}/create`)}
                title="New"
              >
                <i className="bx bx-layer-plus" />
              </Button>
            </>
          )}
        </TableActions>
      </TableHeader>

      <div style={{ overflowX: 'auto' }}>
        <Table>
          <thead>
            <tr>
              {columns.map((column) => (
                <Th key={column.field}>{column.title}</Th>
              ))}
              {!ky && <Th>Actions</Th>}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <Td colSpan={columns.length + (!ky ? 1 : 0)} style={{ textAlign: 'center' }}>
                  Loading...
                </Td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <Td colSpan={columns.length + (!ky ? 1 : 0)} style={{ textAlign: 'center' }}>
                  No records found
                </Td>
              </tr>
            ) : (
              data.map((item, index) => (
                <Tr key={index}>
                  {columns.map((column) => (
                    <Td key={column.field}>{item[column.field]}</Td>
                  ))}
                  {!ky && (
                    <Td>
                      <TableActions>
                        {!isPrivate && standardTxList.some((tx) => tx.tag === 'readAssetHistory') && (
                          <Button
                            variant="secondary"
                            onClick={() => history.push(`/${asset.tag}/list/${item['@key']}`)}
                            title="History"
                          >
                            <i className="bx bx-history" />
                          </Button>
                        )}
                        {standardTxList.some((tx) => tx.tag === 'readAsset') && (
                          <Button 
                            variant="secondary" 
                            onClick={() => handleView(item)}
                            title="View"
                          >
                            <i className="bx bx-show" />
                          </Button>
                        )}
                        {canWrite && standardTxList.some((tx) => tx.tag === 'updateAsset') && (
                          <Button 
                            variant="secondary" 
                            onClick={() => handleEdit(item)}
                            title="Edit"
                          >
                            <i className="bx bx-edit-alt" />
                          </Button>
                        )}
                        {standardTxList.some((tx) => tx.tag === 'deleteAsset') && (
                          <Button 
                            variant="secondary" 
                            onClick={() => handleDelete(item)}
                            title="Delete"
                          >
                            <i className="bx bx-trash" />
                          </Button>
                        )}
                      </TableActions>
                    </Td>
                  )}
                </Tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <TableFooter>
        <Pagination>
          <select value={rowsPerPage} onChange={handleRowsPerPageChange}>
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
          <span>
            Showing {data.length} results
          </span>
          {!ky && (
            <>
              <PaginationButton
                disabled={!hasPreviousPage || isLoading}
                onClick={onPreviousPage}
              >
                <i className="bx bx-chevron-left" />
                Previous
              </PaginationButton>
              <PaginationButton
                disabled={!hasNextPage || isLoading}
                onClick={onNextPage}
              >
                Next
                <i className="bx bx-chevron-right" />
              </PaginationButton>
            </>
          )}
        </Pagination>
      </TableFooter>

      {dialogOpen && (
        <Dialog>
          <DialogContent>
            <DialogHeader>
              <h3>Confirm Delete</h3>
            </DialogHeader>
            <DialogBody>
              <p>Are you sure you want to delete this item?</p>
            </DialogBody>
            <DialogFooter>
              <Button variant="secondary" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={confirmDelete}>
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Container>
  );
};

export default TableGenerator;